import { If, onCleanup, onMount, createEffect, reactive } from 'cdui-js';
import {
  editLastProcess,
  getFormApi,
  getFormValidateFunc,
  pState,
  setFormApi,
  setFormValidateFunc,
} from '../store';
import { createSubmitLock } from '../createSubmitLock';
import { showToast } from '../../../components/Toast';
import i18n from '../../../../../i18n';
import { getEffectiveSafeBottom } from '../../../../../stores/common';
import { canUseKeyboardAvoidJs } from '../keyboardCapability';
import { attachKeyboardAvoid, getBarBottomOffset, keyboardAvoidState } from '../keyboardAvoid';
import { barSlot, setBarSlot, waitForOpenProgressPaint } from './barSlot';

interface Props {
  last?: () => void;
  next?: () => void | Promise<void>;
  loading?: boolean;
  /** 为 true 时禁用「下一步」（提交中仍优先走 isBusy） */
  nextDisabled?: boolean;
  /** 为 true 时不渲染「上一步 / 返回」（如提交成功页仅「完成」） */
  hideLast?: boolean;
  /** 覆盖右侧主按钮文案（如「完成」） */
  nextLabel?: string;
}

const BAR_HEIGHT = 80;

/** PC 430×932 壳：fixed 相对 #h5-shell，滚动在 #main，底栏不随内容走 */
const isPcShell = () =>
  typeof document !== 'undefined' &&
  !!document.getElementById('h5-shell')?.classList.contains('h5-shell--pc');

const safeBottom = () => getEffectiveSafeBottom();
const barTotalHeight = () => BAR_HEIGHT + safeBottom();

type PlaceSnapshot = {
  step: number;
  phase: string;
  editMode: boolean;
  editRefusedMode: boolean;
};

const snapshotPlace = (): PlaceSnapshot => ({
  step: pState.current,
  phase: pState.editRefusedPhase,
  editMode: pState.editMode,
  editRefusedMode: pState.editRefusedMode,
});

const samePlace = (from: PlaceSnapshot) =>
  pState.current === from.step &&
  pState.editRefusedPhase === from.phase &&
  pState.editMode === from.editMode &&
  pState.editRefusedMode === from.editRefusedMode;

/**
 * 转圈颜色跟随所在按钮的 `color`（currentColor）。
 * 上一步是 default-btn，深色模式字色为白；写死深色会融进底。
 * tone 只用来区分两颗同时出现时的渐变 id。
 */
export const OpenProgressBarSpinner = (props: { tone?: 'light' | 'dark' }) => {
  const tone = () => (props.tone === 'dark' ? 'dark' : 'light');
  const id0 = () => `paint0_linear_bar_${tone()}`;
  const id1 = () => `paint1_linear_bar_${tone()}`;

  return (
    <svg
      style={{ animation: 'spin 1s linear infinite' }}
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M10.886 2.06125C11.1496 2.03207 11.414 2.10882 11.6211 2.2746C11.8281 2.44038 11.9608 2.68162 11.99 2.94525C12.0192 3.20887 11.9424 3.47329 11.7766 3.68033C11.6109 3.88737 11.3696 4.02007 11.106 4.04925C9.07764 4.2816 7.21483 5.28056 5.899 6.84158C4.58316 8.40261 3.91385 10.4076 4.0281 12.446C4.14236 14.4844 5.03153 16.4021 6.51357 17.8063C7.99561 19.2105 9.95838 19.995 12 19.9992V21.9992C6.477 21.9992 2 17.5232 2 11.9992C2 6.89325 5.848 2.62125 10.886 2.06225V2.06125Z"
        fill={`url(#${id0()})`}
      />
      <path
        d="M16.322 4.04002C16.4737 3.82259 16.7056 3.67432 16.9667 3.62781C17.2277 3.58131 17.4965 3.64037 17.714 3.79202C19.0378 4.71269 20.119 5.94012 20.8654 7.36946C21.6118 8.79879 22.001 10.3876 22 12C22 17.523 17.523 22 12 22V20C13.7035 20.0003 15.3626 19.4567 16.7358 18.4485C18.1089 17.4403 19.1244 16.0201 19.6343 14.3947C20.1442 12.7693 20.122 11.0236 19.5708 9.41168C19.0197 7.79978 17.9684 6.40591 16.57 5.43302C16.4622 5.35788 16.3703 5.26223 16.2994 5.15154C16.2286 5.04085 16.1803 4.91729 16.1573 4.78792C16.1343 4.65855 16.137 4.52591 16.1652 4.39758C16.1935 4.26925 16.2468 4.14775 16.322 4.04002Z"
        fill={`url(#${id1()})`}
      />
      <defs>
        <linearGradient id={id0()} x1="7" y1="3.10643" x2="7" y2="20.3624" gradientUnits="userSpaceOnUse">
          <stop stop-color="currentColor" />
          <stop offset="1" stop-color="currentColor" stop-opacity="0.55" />
        </linearGradient>
        <linearGradient id={id1()} x1="17" y1="5.24458" x2="17" y2="20.2376" gradientUnits="userSpaceOnUse">
          <stop stop-color="currentColor" stop-opacity="0" />
          <stop offset="1" stop-color="currentColor" stop-opacity="0.55" />
        </linearGradient>
      </defs>
    </svg>
  );
};

/**
 * 步骤内注册器：各步骤组件照旧渲染 `<LastNextBtns last={...} next={...} .../>`，
 * 但本组件不再渲染固定栏本身，只把回调/状态写入共享槽（`barSlot`），并在滚动内容里
 * 保留占位 spacer。真正的固定栏由 `LastNextBarPortal`（挂在 `OpenProcess` 顶层、
 * 滚动容器之外）统一渲染，从而让 `position: fixed` 相对视口生效，修复安卓上底栏随滚动跳动。
 */
export const LastNextBtns = (props: Props) => {
  let detachBarSlot: (() => void) | null = null;

  // 离开当前页时清空 formApi / formValidateFunc 与槽，避免无 Form 的下一步误用上一页校验
  onCleanup(() => {
    setFormApi(null);
    setFormValidateFunc(null);
    detachBarSlot?.();
    detachBarSlot = null;
  });

  // 注册到共享槽：createEffect 在响应式作用内读取 props，props 变化时自动同步
  createEffect(() => {
    detachBarSlot?.();
    detachBarSlot = setBarSlot({
      last: props.last,
      next: props.next,
      loading: props.loading,
      nextDisabled: props.nextDisabled,
      hideLast: props.hideLast,
      nextLabel: props.nextLabel,
    });
  });

  // 仅占位：让滚动内容底部预留底栏高度，避免长表单末尾被固定栏挡住
  return <div class="open-progress-last-next-spacer" style={{ height: `${barTotalHeight()}px` }} />;
};

/**
 * 固定栏 Portal：在 `OpenProcess` 顶层渲染一次（滚动容器之外），读取 `barSlot` 渲染
 * 真正的「上一步/下一步」底栏。键盘避让在此挂载（整页生命周期内单次 attach）。
 */
export const LastNextBarPortal = () => {
  const { lock, run } = createSubmitLock();
  // next：右侧转圈；last：两侧只禁用，上一步不转圈，下一步也不借这次点击转圈。
  const barAction = reactive({ side: '' as '' | 'next' | 'last' });
  // 首屏 getByUserId 回填前禁止提交，避免空表误点。
  // transitioning：纠错底栏交还 Portal 后，目标页画出前继续锁住。
  const isBusy = () =>
    lock.submitting || pState.loading || barSlot.loading() || barSlot.transitioning;
  const showNextLoading = () => isBusy() && barAction.side !== 'last';
  const pcShell = isPcShell();

  let detachKeyboardAvoid: (() => void) | null = null;

  /** CA 双页纠错：身份证（区间首页）不显示返回，仅银行卡显示；或步骤显式 hideLast */
  const hideBack = () =>
    barSlot.hideLast() ||
    (pState.editMode &&
      pState.profilePassDualFix &&
      pState.current === pState.editStartStep &&
      pState.editStartStep < pState.editEndStep);

  /**
   * 确认资料页点「修改」后：
   * - 区间未走完仍用「确定 / 下一步」推进；
   * - 单页或区间末页改为「保存」，只落库、不离开当前页。
   * CA 双页纠错末页仍用「确定」并回风险披露。
   * 步骤可传 nextLabel 覆盖（如提交成功页「完成」）。
   */
  const nextLabel = () => {
    if (showNextLoading()) return null;
    const override = barSlot.nextLabel();
    if (override) return override;
    if (
      pState.editMode &&
      pState.profilePassDualFix &&
      pState.current < pState.editEndStep
    ) {
      return i18n.OpenProgress.Common.Next;
    }
    if (pState.editMode && pState.profilePassDualFix) return i18n.OpenProgress.Common.Confirm;
    if (pState.editMode && pState.current >= pState.editEndStep) {
      return i18n.OpenProgress.Common.Save;
    }
    if (pState.editMode) return i18n.OpenProgress.Common.Confirm;
    return i18n.OpenProgress.Common.Next;
  };

  onCleanup(() => {
    detachKeyboardAvoid?.();
    detachKeyboardAvoid = null;
  });

  // 软键盘 / 内容滚动时驱动底栏与 focus 滚入可视区；capability=none 时放弃
  onMount(() => {
    if (pcShell || !canUseKeyboardAvoidJs()) return;
    detachKeyboardAvoid = attachKeyboardAvoid();
  });

  const handleNext = () => {
    if (isBusy() || barSlot.nextDisabled()) return;
    barAction.side = 'next';
    void run(async () => {
      const from = snapshotPlace();
      // 1) Form 必填项：失败仅 toast，不进入各页 next（避免空表触发保存）
      const api = getFormApi();
      if (api) {
        const valid = await api.validate();
        if (!valid) {
          // 缺 i18n key 时仍提示完善信息，勿回落「服务异常」
          showToast(i18n.OpenProgress?.Common?.ToastIncomplete || '请完善必填信息');
          api.scrollToError?.();
          return;
        }
      }
      // 2) 自定义校验（勾选/上传凭证等）：回调内自行 toast，此处不再重复
      const formValidateFunc = getFormValidateFunc();
      if (formValidateFunc) {
        const valid = await formValidateFunc.callback();
        if (!valid) return;
      }
      await barSlot.next?.();
      // 校验失败 / 编辑「保存」留在本页：步骤没变，立即解锁。
      // 已经切页：锁保持到目标页画出，避免连点把访问栈再推几格。
      if (!samePlace(from)) await waitForOpenProgressPaint();
    }).finally(() => {
      barAction.side = '';
    });
  };

  // 编辑模式下「返回」：区间内回退或取消修改（首页还原快照）后回到确认页
  // profilePassDualFix 银行卡页 → 直接回 RiskInfo（见 editLastProcess）
  const handleLast = () => {
    if (isBusy() || hideBack()) return;
    barAction.side = 'last';
    void run(async () => {
      const from = snapshotPlace();
      if (pState.editMode) {
        editLastProcess();
      } else {
        await barSlot.last?.();
      }
      if (!samePlace(from)) await waitForOpenProgressPaint();
    }).finally(() => {
      barAction.side = '';
    });
  };

  return (
    <If when={barSlot.active && !barSlot.suppressed}>
      <div
        class={`open-progress-last-next${pcShell ? ' open-progress-last-next--pc' : ''}`.trim()}
        style={{
          // 键盘打开时 visibility 隐藏，避免卸载/重挂导致布局跳变（配合固定滚动区高度）
          visibility: keyboardAvoidState.keyboardOpen ? 'hidden' : 'visible',
          'pointer-events': keyboardAvoidState.keyboardOpen ? 'none' : 'auto',
        }}
      >
        {/* 提交中：盖住页面禁止输入，低于按钮栏(150)、高于页面内容 */}
        <If when={isBusy()}>
          <div
            class="open-progress-submit-mask fixed position-t position-b position-l position-r"
            style={{ 'z-index': 140 }}
            onclick={(e) => e.stopPropagation()}
          />
        </If>

        <div
          class={`open-progress-last-next-bar flex padding-x-L align-center width-100 bg-c fixed position-b position-l${
            pcShell ? ' open-progress-last-next-bar--pc' : ''
          }`.trim()}
          style={{
            gap: '10px',
            height: `${barTotalHeight()}px`,
            'padding-bottom': safeBottom() ? `${safeBottom()}px` : undefined,
            'box-sizing': 'border-box',
            /* 高于表单清除/上传删除等（z-index:100），低于 ActionSheet(199)/Modal(299) */
            'z-index': 150,
            /* 键盘弹起时整栏 visibility:hidden；贴底 offset 仍可供 PC 壳复用 */
            bottom: `${getBarBottomOffset()}px`,
          }}
        >
          <If when={!hideBack()}>
            <button
              class="default-btn btn-rounded"
              style={{ height: '50px', flex: 1 }}
              onclick={handleLast}
              disabled={isBusy()}
            >
              {pState.editMode ? i18n.OpenProgress.Common.Back : i18n.OpenProgress.Common.Prev}
            </button>
          </If>

          <button
            class="primary-btn btn-rounded"
            style={{ flex: hideBack() ? 1 : 2 }}
            onclick={handleNext}
            disabled={isBusy() || !!barSlot.nextDisabled()}
          >
            {showNextLoading() ? <OpenProgressBarSpinner /> : nextLabel()}
          </button>
        </div>
      </div>
    </If>
  );
};
